import { fmt, ScenarioSchema, type CalcResult, type Scenario } from "@h2lca/core";
import { useMemo, useState } from "react";
import { resultSummary, useWorkshop } from "@/store/workshop";
import { StageBadge } from "../Badges";
import { GlossaryTip } from "../GlossaryTip";
import { Section } from "../Section";
import { AllocationEditor } from "./AllocationEditor";
import { ElectricityEditor } from "./ElectricityEditor";
import { EquipmentInputsEditor } from "./EquipmentInputsEditor";
import { LicensedAction } from "@/components/licensing/LicensedAction";
import { ImportPanel } from "@/components/workspace/ImportPanel";
import type { ImportRecord } from "@/components/workspace/import-template";
import { suggestImportMapping } from "../import-mapping";
import { api } from "@/lib/api";

/** ⑤ 운전자료 입력 , 전력 믹스,부산물 배분,설비별 활동자료 */
export function InputsStep({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const selectedId = useWorkshop((s) => s.selectedEquipmentId);
  const select = useWorkshop((s) => s.select);
  const setStep = useWorkshop((s) => s.setStep);
  const serverId = useWorkshop((s) => s.serverId);
  const loadScenario = useWorkshop((s) => s.loadScenario);
  const saveToServer = useWorkshop((s) => s.saveToServer);
  const [importedRecords, setImportedRecords] = useState<ImportRecord[]>([]);
  const [latestImportId, setLatestImportId] = useState<string | null>(null);
  const [importNotice, setImportNotice] = useState<string | null>(null);
  const mapping = useMemo(() => suggestImportMapping(importedRecords, scenario), [importedRecords, scenario]);
  const ordered = [...scenario.equipment].sort((a, b) => a.order - b.order);
  const eq = ordered.find((e) => e.id === selectedId) ?? ordered[0] ?? null;

  async function ensureScenarioId() {
    const current = useWorkshop.getState();
    if (current.serverId) return current.serverId;
    return saveToServer(resultSummary(result));
  }

  async function applyImportedData() {
    if (mapping.matches.length === 0) {
      setImportNotice("현재 시나리오에 자동으로 연결할 수 있는 행이 없습니다. 설비명, 활동자료명, 단위를 확인해 주세요.");
      return;
    }
    const current = useWorkshop.getState();
    if (!current.serverId || !latestImportId || !current.scenario) {
      setImportNotice("파일을 저장한 뒤 산정 입력값 반영을 다시 시도해 주세요.");
      return;
    }
    try {
      const response = await api<{ scenario: unknown; updatedAt: string; application: { appliedRows: number; skippedRows: number; issues: string[] } }>(`/scenarios/${current.serverId}/imports/${latestImportId}/apply`, {
        method: "POST",
        json: { scenario: current.scenario, mappings: mapping.matches.map((match) => ({ rowIndex: match.rowIndex, equipmentId: match.equipmentId, inputId: match.inputId })) },
      });
      const parsed = ScenarioSchema.safeParse(response.scenario);
      if (!parsed.success) throw new Error("반영된 산정 시나리오를 읽을 수 없습니다.");
      loadScenario(parsed.data, current.serverId, response.updatedAt);
      setImportNotice(`${response.application.appliedRows}개 행을 산정 입력값에 반영했습니다. ${response.application.skippedRows}개 행은 검토 대상으로 남겼습니다.`);
    } catch (e) {
      setImportNotice(e instanceof Error ? e.message : "검증한 값을 산정 입력값에 반영하지 못했습니다.");
    }
  }

  return (
    <Section
      title={
        <>
          운전자료 입력 <GlossaryTip k="activity_data" />
        </>
      }
      desc="전력원 구성과 부산물 배분을 정하고, 설비마다 사용량을 채웁니다. 기본값, 사용자 입력, 추정값, 자료 미입력을 구분해 표시합니다."
      aside={<span className="badge-muted">파일 점검과 저장은 교육용 포함</span>}
    >
      <ImportPanel
        scenarioId={serverId}
        ensureScenarioId={ensureScenarioId}
        title="활동자료 파일 점검"
        description="표준 CSV 또는 XLSX를 업로드해 행별 품질을 확인하고 저장합니다. 교육용에서는 여기까지 사용할 수 있으며, 산정 입력값 반영은 기업용 기능입니다."
        badge="교육용 포함"
        onImported={(imported, records) => {
          setLatestImportId(imported.id);
          setImportedRecords(records);
          setImportNotice(`${imported.fileName}을 저장했습니다. 정상 ${imported.validRows}행, 검토 ${imported.warningRows}행, 오류 ${imported.errorRows}행입니다.`);
        }}
      />

      {importedRecords.length > 0 && (
        <section className="rounded-card border border-line-soft bg-surface-inset p-content">
          <div className="flex flex-wrap items-start justify-between gap-content">
            <div>
              <h3 className="text-caption font-semibold text-ink">산정 입력값 연결</h3>
              <p className="mt-label text-footnote text-ink-subtle">설비명, 활동자료명, 단위가 명확히 일치하는 행만 자동 연결 후보로 제시합니다.</p>
            </div>
            <LicensedAction
              feature="import_apply"
              label="검증한 값을 산정 입력값에 반영"
              title="산정 입력값 반영"
              description="교육용에서는 파일 품질 확인과 저장까지 사용할 수 있습니다. 검증한 행을 현재 시나리오에 반영하는 기능은 기업용입니다."
              onAvailable={() => { void applyImportedData(); }}
            />
          </div>
          <div className="mt-content flex flex-wrap gap-item text-footnote">
            <span className="badge-accent">자동 연결 후보 {mapping.matches.length}개</span>
            {mapping.unmatched.length > 0 && <span className="badge-review">확인 필요 {mapping.unmatched.length}개</span>}
          </div>
          {importNotice && <p className="note note-ok mt-content text-footnote" role="status">{importNotice}</p>}
          {mapping.unmatched.length > 0 && (
            <details className="mt-content text-footnote text-ink-subtle">
              <summary className="cursor-pointer font-semibold text-ink">자동 반영하지 않은 행 보기</summary>
              <ul className="mt-item flex flex-col gap-label pl-section">
                {mapping.unmatched.slice(0, 8).map((item, index) => <li key={`${item.record.activity}-${index}`}>{item.record.equipment || "설비 미지정"}, {item.record.activity || "활동자료 미지정"}: {item.reason}</li>)}
              </ul>
            </details>
          )}
        </section>
      )}

      <div className="ws-paired-panels">
        <ElectricityEditor scenario={scenario} result={result} />
        <AllocationEditor scenario={scenario} result={result} />
      </div>

      <div className="ws-input-layout">
        <div className="ws-equipment-list">
          <p className="flex items-center gap-label text-caption font-semibold text-ink">
            설비별 운전자료 <GlossaryTip k="data_quality" />
          </p>
          {ordered.length === 0 && (
            <p className="note note-warn text-footnote">
              설비가 없습니다.{" "}
              <button type="button" className="underline" onClick={() => setStep("equipment")}>
                설비 구성으로
              </button>
            </p>
          )}
          {ordered.map((e) => {
            const r = result.byEquipment.find((x) => x.equipmentId === e.id);
            const errs = result.issues.filter((i) => i.equipmentId === e.id && i.level === "error").length;
            const warns = result.issues.filter((i) => i.equipmentId === e.id && i.level === "warning").length;
            return (
              <button key={e.id} type="button" className="eq-list-item" aria-current={eq?.id === e.id} onClick={() => select(e.id)}>
                <span className="font-mono text-ink-subtle">{e.order + 1}</span>
                <StageBadge stage={e.stage} />
                <span className={`min-w-0 flex-1 truncate ${r?.inBoundary ? "" : "text-ink-subtle line-through"}`}>{e.name}</span>
                {errs > 0 && <span className="badge-soft badge-danger">{errs}</span>}
                {errs === 0 && warns > 0 && <span className="badge-soft badge-warn">{warns}</span>}
                <span className="font-mono text-micro text-ink-muted">{r?.inBoundary ? fmt(r.kgCO2eq) : "없음"}</span>
              </button>
            );
          })}
        </div>
        <div className="ws-input-editor">{eq ? <EquipmentInputsEditor key={eq.id} scenario={scenario} result={result} eq={eq} /> : <p className="text-footnote text-ink-subtle">설비를 선택하세요.</p>}</div>
      </div>
    </Section>
  );
}
